import { getProductByID } from '@adornis/buchungsheld-db/api/product.js';
import { getBHPaymentByID } from '@adornis/buchungsheld-db/api/payment.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { getMailTemplateForInstitution } from '@adornis/buchungsheld-db/api/mail-templates.js';
import { paymentPluginDemoPay, getDemoInvoiceByID } from '../api/demo-payment-plugin.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { scopedCollection } from '@adornis/bh-util/server/scoped-collection.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { getParticipationByID, getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import type { PaymentPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { BHInvoice } from '@adornis/buchungsheld-db/entities/BHInvoice.js';
import { BHPayment } from '@adornis/buchungsheld-db/entities/BHPayment.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import {
  getEmailVarsFromEinzelberatung,
  getEmailVarsFromUser,
  getEmailVarsFromWorkshop,
} from '@adornis/buchungsheld-db/entities/mail-helper.js';
import { InstitutionMailTemplate, MailTemplateType } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { assembleInstitutionMail } from '@adornis/buchungsheld-db/mails/assemble-mail.js';
import { fileAttachment } from '@adornis/buchungsheld-db/mails/file-attachment.js';
import { sendInstitutionEmail } from '@adornis/buchungsheld-db/mails/sendInstitutionMail.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { Price } from '@adornis/payments/db/prices.js';
import type { TemplateResult } from 'lit';
import { DemoStammdaten } from '../stammdaten/demo-stammdaten-plugin.js';
import { DemoInvoiceReceiverEnum } from './invoice-receiver.js';
import { defaultPaymentPlugin } from './default.js';

@Entity()
export class DemoInvoice extends BHInvoice {
  static override _class = 'DemoInvoice';

  // pdf file path of interal-invoice version for accouning
  @Field(type => String)
  pdfInternalID!: string;
}

export const getProductPrice: PaymentPlugin<'invoice', 'default'>['getProductPrice'] = async (productID, userID) => {
  const product = await getProductByID(productID)({
    prices: { ...selectionSet(() => Price, 2), _id: 1, unitAmount: 1, currency: 1 },
  });
  if (!product) throw new Error('could not find product ' + productID);
  return product?.prices?.[0];
};

export const demoPaymentPlugin: PaymentPlugin<'invoice', 'default'> = {
  id: 'payment::demo',
  type: 'payment',
  paymentMethods: ['invoice'],
  getProductPrice,
  createPayment: async (participationIDs: string[], _paymentMethod) => {
    const user = await getCurrentUserServer();
    if (!user) throw new Error('user not found');

    const participations = await getParticipationsByID(participationIDs)({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const institutionID = participations[0]?.product?.institutionID;
    if (!institutionID) throw new Error('something went wrong');

    const paymentIDs = await paymentPluginDemoPay(institutionID, participations)();
    return paymentIDs;
  },

  pay: async (paymentID, _paymentMethod) => {
    // nothing to do here; invoice gets paid manually by user
  },

  afterEnrollment: async participationID => {},

  sendInvoice: async (invoiceID: string) => {
    const invoice = await getDemoInvoiceByID(invoiceID)({ _id: 1, paymentID: 1, pdfID: 1, pdfInternalID: 1 });
    if (!invoice) throw new Error('invoice not found');

    logger.info('SENDING INVOICE FROM DEMO PAYMENT PLUGIN');
    const payment = await getBHPaymentByID(invoice.paymentID)({
      _id: 1,
      createdAt: 1,
      participationIDs: 1,
      status: 1,
      orderNr: { year: 1, nr: 1 },
    });
    if (!payment) throw new Error('should not happen');

    const participations = await getParticipationsByID(payment.participationIDs)({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
    }).then(ps => ps.filter(isDefined));

    const p = participations[0];
    if (!p) throw new Error('participations required');

    if (p.state === ParticipationState.waiting) {
      logger.debug('invoice not send because user is on waiting list');
      return false;
    }

    const user = p.user;
    if (!user) throw new Error('user is missing');

    const product = p.product;
    if (!product) throw new Error('product not found');

    const institutionID = product.institutionID;

    const collection = await scopedCollection<DemoStammdaten>(UserStammdaten._class);
    const stammdaten = await collection.findOne({ userID: user._id, institutionID });
    if (!stammdaten) throw new Error('stammdaten not found');

    const sendMail = async (
      template: MailTemplateType,
      invoiceFileID: string,
      receiverMail: string,
      cc: Maybe<string>,
    ) => {
      const mailTemplate = await getMailTemplateForInstitution(
        institutionID,
        template,
      )(InstitutionMailTemplate.allFields as any);

      logger.info({ emailNot: mailTemplate.emailNotification, emailID: mailTemplate._id, mailTemplate }, 'HÄ1');
      if (!mailTemplate.emailNotification) return;

      const emailVars = (() => {
        if (product instanceof Workshop) return getEmailVarsFromWorkshop(product, { ...user, title: user.title });
        else if (product instanceof Einzelberatung)
          return getEmailVarsFromEinzelberatung(product, { ...user, title: user.title });
        else return getEmailVarsFromUser({ ...user, title: user.title });
      })();

      logger.info({ emailVars }, 'HÄ2');

      const { content: mailContent } = await assembleInstitutionMail({ template: mailTemplate, infos: emailVars });

      if (!mailContent) throw new Error('could not build email');
      await sendInstitutionEmail({
        email: receiverMail,
        subject: mailTemplate.subject,
        content: mailContent as TemplateResult<1>,
        institutionID,
        attachments: [await fileAttachment(invoiceFileID, 'Rechnung.pdf')],
        cc,
      });
    };

    const sendUserMail = (userMail: string, cc: Maybe<string> = null) =>
      sendMail(MailTemplateType.DEMO_INVOICE_USER, invoice.pdfID, userMail, cc);
    const sendInstitutionMail = (institutionMail: string) =>
      sendMail(
        MailTemplateType.DEMO_INVOICE_INSTITUTION,
        invoice.pdfInternalID,
        institutionMail,
        null,
      );

    const isIntern = (stammdaten as any).invoiceReceiver === DemoInvoiceReceiverEnum.DEMOINTERNAL;
    const isExtern = (stammdaten as any).invoiceReceiver === DemoInvoiceReceiverEnum.DEMOEXTERNAL;

    // user mail
    if (isIntern)
      void 0; // do nothing
    else if (isExtern) await sendUserMail(user.email);
    else await sendUserMail(user.email);

    // institution mail
    await (async () => {
      const s = p.product?.settings;
      if (!s) {
        logger.warn('no settings found; cant send mail');
        return;
      }
      const institutionMail1 = s.interAccountingEmail1 ?? s.inheritedInterAccountingEmail1?.[1];
      const institutionMail2 = s.interAccountingEmail2 ?? s.inheritedInterAccountingEmail2?.[1];
      const institutionMail3 = s.interAccountingEmail3 ?? s.inheritedInterAccountingEmail3?.[1];
      const institutionMails = [institutionMail1, institutionMail2, institutionMail3]
        .filter(it => !!it)
        .filter(isDefined);
      if (institutionMails.length === 0) {
        logger.warn('no institution-mail available; cant send institution-invoice-copy');
        return;
      }

      for (const institutionMail of institutionMails) {
        await sendInstitutionMail(institutionMail);
      }
    })();

    payment.status = 'issued';
    await payment.save();
    return true;
  },
  cancleEnrollment: async (participationID: string) => {
    const participation = await getParticipationByID(participationID)({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      signoffDate: 1,
      registrationDate: 1,
      isNoShow: 1,
      currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const payment = participation.currentPayment as Maybe<BHPayment>;
    if (!payment) return;
    switch (payment.status) {
      case 'paid':
        payment.status = 'refund';
        await payment.save();
        return;
      default:
        await defaultPaymentPlugin.cancleEnrollment(participationID);
        return;
    }
  },

  priceGroups: ['default'],
};
